import { useEffect, useMemo, useRef, useState } from "react";
import { usePdfZoomPan } from "../lib/usePdfZoomPan";
import { expenseCellBboxPct, type ExpenseFieldName } from "../lib/expenseCellBbox";
import { spatialCellBboxPct, type ExpenseSpatial } from "../lib/expenseSpatial";

/**
 * Diäten-Pendant zu PdfWithHighlights (Stundenliste). Rendert die Expense-PDF-
 * Vorschau mit absolut positionierten Overlay-Divs — eine pro (Zeilen-Index, Feld) —
 * die bei Hover die zugehörige Datentabellen-Zelle und umgekehrt hervorheben
 * (bidirektionaler Link über `activeCell` + data-row/data-field-Attribute).
 *
 * Geometrie aus expenseCellBbox.ts (Spalten 1:1 aus _naive_expense_grid.col_pct).
 * Zeilen = Entry-Index über das Tabellen-Band (Heuristik; pixel-genaue Zeilen via
 * cv2-Grid-Detect später, analog timesheet). Gleiche CSS-Klassen wie die
 * Stundenliste, damit Styling konsistent ist.
 */
interface Props {
  imageUrl: string;
  pdfHrefForOpen: string;
  /** Anzahl Zeilen (= Anzahl extrahierter Entries), für die Overlays gerendert werden. */
  rowCount: number;
  /** Spalten, die als Overlay-Zellen gerendert werden (i.d.R. die sichtbaren
   *  Tabellenfelder). String statt ExpenseFieldName: Karte 592 — das
   *  Fahrtenbuch bringt eigene Feld-Keys + eigene Geometrie (bboxForCell). */
  fields: ReadonlyArray<string>;
  activeCell: { row: number; field: string } | null;
  onCellHover: (cell: { row: number; field: string } | null) => void;
  className?: string;
  interactive?: boolean;
  /** Homography-projizierte Geometrie (meta.spatial). Wenn gesetzt, ersetzt
   *  sie die statische Heuristik — Caller muss die Preview dann mit
   *  rotate=spatial.rotationAppliedCw laden (gleicher Frame!). */
  spatial?: ExpenseSpatial | null;
  /** Karte 592: typ-eigene Zell-Geometrie (z. B. Fahrtenbuch meta.fahrtenbuch_geom
   *  — Lese-Fenster der OCR = Markierung, Glass-Box). Hat Vorrang vor
   *  spatial/Heuristik. */
  bboxForCell?: (row: number, field: string) => { topPct: number; leftPct: number; widthPct: number; heightPct: number } | null;
  /** Feuert, sobald die Vorschau geladen UND die Overlay-Geometrie berechnet
   *  ist (oder bei Lade-Fehler). Der Caller nutzt das, um die Seite bis dahin
   *  zu sperren — VOR dem Load sitzen die Rahmen sonst kurz falsch (imgBox=null
   *  → Fallback aufs ganze Rechteck) und verleiten zu Fehleingaben. */
  onReady?: () => void;
}

export function ExpensePdfHighlights({
  imageUrl,
  pdfHrefForOpen,
  rowCount,
  fields,
  activeCell,
  onCellHover,
  className,
  interactive = true,
  spatial = null,
  bboxForCell,
  onReady,
}: Props) {
  // Bild wird mit object-fit:contain gerendert → Letterboxing wenn Container-
  // Aspect ≠ Bild-Aspect. Overlays nach % des CONTAINERs würden bei Resize
  // driften. Wir tracken die tatsächliche Bild-Bounding-Box (ResizeObserver +
  // naturalWidth/Height) und legen die Overlay-Ebene exakt aufs Bild-Rechteck.
  // (Identisch zur Stundenliste — dort war Resize-Drift ein realer Bug.)
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgBox, setImgBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  // Bis das Vorschaubild geladen ist, sind naturalWidth/Height = 0 → imgBox
  // bliebe null → die Overlays fielen auf das ganze Container-Rechteck zurück
  // und säßen FALSCH. Deshalb: Rahmen erst zeigen, wenn `loaded` — und dem
  // Caller per onReady signalisieren, dass die Seite jetzt sicher bedienbar ist.
  const [loaded, setLoaded] = useState(false);

  // Neue Vorschau (Karte/Rotation gewechselt) → wieder in den Lade-Zustand.
  useEffect(() => { setLoaded(false); setImgBox(null); }, [imageUrl]);

  useEffect(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;
    const compute = () => {
      const nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return;
      const cw = container.clientWidth, ch = container.clientHeight;
      if (!cw || !ch) return;
      const naturalRatio = nw / nh;
      const containerRatio = cw / ch;
      let rw, rh, ox, oy;
      if (containerRatio > naturalRatio) {
        rh = ch; rw = ch * naturalRatio; ox = (cw - rw) / 2; oy = 0;
      } else {
        rw = cw; rh = cw / naturalRatio; ox = 0; oy = (ch - rh) / 2;
      }
      setImgBox({ left: ox, top: oy, width: rw, height: rh });
    };
    const markReady = () => { compute(); setLoaded(true); onReady?.(); };
    compute();
    // Cache-Fall: das Bild kann schon fertig sein, bevor der Listener hängt
    // (dann feuert "load" nie) → sofort als bereit behandeln.
    if (img.complete && img.naturalWidth) markReady();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    img.addEventListener("load", markReady);
    // Lade-Fehler (z.B. Preview-Render 5xx): Seite NICHT ewig sperren —
    // Vorhang lösen, der User kann die Tabelle trotzdem bearbeiten.
    img.addEventListener("error", markReady);
    return () => {
      ro.disconnect();
      img.removeEventListener("load", markReady);
      img.removeEventListener("error", markReady);
    };
  }, [imageUrl, onReady]);

  const overlays = useMemo(() => {
    const list: Array<{ row: number; field: string; style: React.CSSProperties; isActive: boolean }> = [];
    for (let row = 0; row < rowCount; row++) {
      for (const field of fields) {
        const b = bboxForCell
          ? bboxForCell(row, field)
          : spatial
          ? spatialCellBboxPct(spatial, row, field as ExpenseFieldName)
          : expenseCellBboxPct(row, field as ExpenseFieldName);
        if (!b) continue;
        list.push({
          row, field,
          style: { top: `${b.topPct}%`, left: `${b.leftPct}%`, width: `${b.widthPct}%`, height: `${b.heightPct}%` },
          isActive: activeCell?.row === row && activeCell?.field === field,
        });
      }
    }
    return list;
  }, [rowCount, fields, activeCell, spatial, bboxForCell]);

  const overlayLayerStyle: React.CSSProperties = imgBox
    ? { position: "absolute", left: imgBox.left, top: imgBox.top, width: imgBox.width, height: imgBox.height }
    : { position: "absolute", inset: 0 };

  // Zoom+Pan (geteilter Hook, Muster PdfWithHighlights — Thomas 24.07.)
  const zp = usePdfZoomPan();

  return (
    <div ref={containerRef}
         className={`pdf-with-highlights${className ? " " + className : ""}${interactive ? "" : " readonly"}${zp.zoom > 1.01 ? " zoomed" : ""}`}
         onWheel={zp.onWheel}>
      <div className="pdf-zoom-wrapper"
           style={{ transform: zp.transform, transformOrigin: "0 0" }}
           onPointerDown={zp.onPointerDown} onPointerMove={zp.onPointerMove}
           onPointerUp={zp.onPointerUp} onPointerCancel={zp.onPointerUp}>
      <a href={pdfHrefForOpen} target="_blank" rel="noopener noreferrer" title="PDF in neuem Tab öffnen" className="pdf-link"
         onClick={(e) => { if (zp.zoom > 1.01) e.preventDefault(); }}>
        <img ref={imgRef} src={imageUrl} alt="PDF-Vorschau" className="pdf-image" loading="eager" decoding="async" draggable={false} />
      </a>
      {interactive && loaded && imgBox && (
        <div className="pdf-overlay-layer" style={overlayLayerStyle} aria-hidden="true">
          {overlays.map(({ row, field, style, isActive }) => (
            <div
              key={`${row}-${field}`}
              className={`pdf-overlay-cell ${spatial ? "src-spatial" : "src-heuristic"}${isActive ? " active" : ""}`}
              data-row={row}
              data-field={field}
              style={style}
              onPointerEnter={() => onCellHover({ row, field })}
              onPointerLeave={() => onCellHover(null)}
            />
          ))}
        </div>
      )}
      </div>
      <div className="pdf-zoom-controls" aria-label="Zoom-Steuerung">
        <button type="button" onClick={() => zp.setZoom((z) => zp.clampZoom(z / 1.25))} disabled={zp.zoom <= 1.01} title="Zoom out">−</button>
        <span className="pdf-zoom-level" aria-live="polite">{Math.round(zp.zoom * 100)}%</span>
        <button type="button" onClick={() => zp.setZoom((z) => zp.clampZoom(z * 1.25))} disabled={zp.zoom >= 3.99} title="Zoom in">+</button>
        <button type="button" onClick={zp.resetZoom} disabled={zp.zoom <= 1.01 && zp.pan.x === 0 && zp.pan.y === 0} title="Reset">⟲</button>
      </div>
    </div>
  );
}
